// Running pre-tax estimate, shown the moment a machine is in the cart and BEFORE
// the customer clicks Calculate. Tax + final total aren't known yet (no address),
// so we show the pre-tax subtotal (net of any active promo/discount) plus a
// monthly computed on that pre-tax amount — no assumed tax. Once Calculate runs,
// the full Summary (real tax + total) takes over and this hides.

import { CREDIT_APRS, useConfigurator } from "../store";
import {
  amortizedMonthlyCents,
  formatDollarsAndCents,
  formatMonthly,
} from "../format";

/** Returns the running numbers, or null when there's nothing to show yet
 *  (no machine) or the real Summary has taken over (calculated). */
function useRunningEstimate() {
  const {
    state,
    selectedSubtotalCents,
    tradeInCents,
    promoBanner,
    outsideLower48,
  } = useConfigurator();
  // No price for a non-serviceable address — the contact-Sales notice shows.
  if (!state.selectedMachineId || state.calculated || outsideLower48) return null;

  const emp = state.employee;
  const empDiscountCents =
    emp.active && emp.discountType
      ? emp.discountType === "percent"
        ? Math.round((selectedSubtotalCents * (emp.discountValue || 0)) / 100)
        : Math.min(
            Math.round((emp.discountValue || 0) * 100),
            selectedSubtotalCents,
          )
      : 0;
  const promoCents =
    promoBanner.status === "applied" ? promoBanner.savingsCents : 0;
  const discountCents = promoCents + empDiscountCents;
  const netSubtotalCents = Math.max(
    0,
    selectedSubtotalCents - discountCents - tradeInCents,
  );

  // Monthly runs on the pre-tax subtotal (no assumed tax); the address sets the
  // real tax at Calculate.
  const principal = Math.max(0, netSubtotalCents - state.financing.downPaymentCents);
  const monthlyCents = amortizedMonthlyCents(
    principal,
    CREDIT_APRS[state.financing.creditTier],
    state.financing.termMonths,
  );

  const discountLabel =
    promoCents > 0 && empDiscountCents > 0
      ? "Savings"
      : promoCents > 0
        ? "Promo savings"
        : "Discount";

  return {
    netSubtotalCents,
    discountCents,
    discountLabel,
    tradeInCents,
    monthlyCents,
  };
}

/** Full card — desktop, at the top of the config column. */
export function RunningEstimate() {
  const est = useRunningEstimate();
  if (!est) return null;
  return (
    <div className="rounded-lg border border-line bg-mist/40 px-4 py-3">
      <div className="flex items-baseline justify-between">
        <span className="font-sans text-[13px] font-semibold uppercase tracking-tag text-graphite">
          Subtotal <span className="normal-case text-steel">(before tax)</span>
        </span>
        <span className="font-display text-xl font-extrabold text-ink">
          {formatDollarsAndCents(est.netSubtotalCents)}
        </span>
      </div>
      {est.discountCents > 0 && (
        <div className="mt-0.5 flex items-baseline justify-between font-sans text-[13px] text-graphite">
          <span>{est.discountLabel}</span>
          <span>−{formatDollarsAndCents(est.discountCents)}</span>
        </div>
      )}
      {est.tradeInCents > 0 && (
        <div className="mt-0.5 flex items-baseline justify-between font-sans text-[13px] text-graphite">
          <span>Trade-in credits</span>
          <span>−{formatDollarsAndCents(est.tradeInCents)}</span>
        </div>
      )}
      <div className="mt-1 flex items-baseline justify-between">
        <span className="font-sans text-[13px] text-steel">Est. monthly</span>
        <span className="font-sans text-[15px] font-bold text-ink">
          ~{formatMonthly(est.monthlyCents)}*
        </span>
      </div>
      <p className="mt-1.5 font-serif text-[12px] italic text-steel">
        * Estimated before tax. Your delivery address sets the exact tax and
        total.
      </p>
    </div>
  );
}

/** Compact single line — mobile sticky bar, above the CTA. */
export function RunningEstimateCompact() {
  const est = useRunningEstimate();
  if (!est) return null;
  return (
    <div className="mb-2 flex items-baseline justify-between border-b border-line pb-2">
      <span className="font-sans text-[13px] text-graphite">
        Subtotal <span className="text-[11px] text-steel">before tax</span>
      </span>
      <span className="font-sans text-[14px] font-bold text-ink">
        {formatDollarsAndCents(est.netSubtotalCents)}
        <span className="font-normal text-steel">
          {" "}
          · ~{formatMonthly(est.monthlyCents)}
        </span>
      </span>
    </div>
  );
}
